import { default as defaultLocalSettings } from '../defaultLocalSettings.json';
import { PlatformType } from '../office/PlatformType';
import { HostType } from '../office/HostType';
import { AuthenticationMode } from './serviceContainer';

class LocalSettingsService {
    private localSettings: LocalSettings;
    private defaultSettings: LocalSettings;
    private fetchedSettings: DeepPartial<LocalSettings> | null = null;
    private loadPromise: Promise<void> | null = null;

    constructor() {
        this.localSettings = this.defaultSettings = defaultLocalSettings as LocalSettings;
    }

    private deepMerge<T>(target: T, source: DeepPartial<T> | null): T {
        if (typeof target !== 'object' || target === null) {
            throw 'Target must be a valid object and cannot be null';
        }

        if (source === null) {
            return target; // If source is null, return the target as-is
        }

        const result: T = { ...target }; // Create a shallow copy of the target

        for (const key in source) {
            if (source[key] !== undefined) {
                if (typeof source[key] === 'object' && !Array.isArray(source[key]) && source[key] !== null) {
                    // Recursively merge nested objects
                    result[key] = this.deepMerge(target[key], source[key] as DeepPartial<T[typeof key]>);
                } else {
                    // Directly assign non-object values
                    result[key] = source[key] as T[typeof key];
                }
            }
        }

        return result;
    }

    async loadLocalSettings(forceRefresh = false) {
        if (forceRefresh) {
            this.loadPromise = null;
            this.fetchedSettings = null;
        }

        // If the loadPromise exists, return it instead of creating a new fetch request
        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = (async () => {
            try {
                const response = await fetch(`/localSettings.json`);
                this.fetchedSettings = await response.json();
                this.localSettings = this.deepMerge(this.defaultSettings, this.fetchedSettings);
            } catch (error) {
                console.error('Failed to load app settings:', error);
                // Reset loadPromise to null so future calls can attempt to load settings again
                this.loadPromise = null;
                throw error; // Rethrow the error to handle it in the calling code if necessary
            }
        })();

        return this.loadPromise;
    }

    // method to override settings in unit tests
    overrideLocalSettings(localSettingsOverride: DeepPartial<LocalSettings>) {
        this.localSettings = this.deepMerge(
            this.deepMerge(this.defaultSettings, this.fetchedSettings),
            localSettingsOverride,
        );
    }

    getLocalSettings() {
        if (!this.fetchedSettings) {
            console.warn('Settings were not loaded so default values will be used.');
        }
        return this.localSettings;
    }
}

export interface LocalSettings {
    useConsoleAuditer: boolean;
    logPerformanceTimings: boolean;
    customActionTimeout: number;
    maxNewDocumentAgeSeconds: number;
    useFortraCloudServices: boolean;
    selfHostedServicesUrl: string;
    authenticationMode: AuthenticationMode;
    enabledPlatformApplications: enabledPlatformApplication;
    clientId: string;
    officeJsLocation: string;
    officeJsSriHash?: string;
    loginAuthority: string;
    loginTenantId: string;
    useNestedAppAuth: boolean;
    graphEndpoint: string;
    portionMarking: portionMarkingSettings;
    dcsOneAboutInfo: dcsOneAboutInfo;
}

interface portionMarkingSettings {
    enabled: boolean;
    disabledTitle: string;
    disabledText: string;
}

interface dcsOneAboutInfo {
    version: string;
}

type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type enabledPlatformApplication = {
    [key in PlatformType]?: HostType[] | undefined;
};

export const localSettingsService = new LocalSettingsService();
